import { unwrapResult } from '@reduxjs/toolkit';
import * as t from 'io-ts';
import { store } from '../store';
import { getSessionList, deleteSession as deleteCallThunk } from '../store/sessions/sessions.thunk';
import {
  startOutboundCall as callOutThunk,
  dialBack as dialBackThunk,
  hold as holdThunk,
  unHold as unHoldThunk,
  transferCall as transferThunk,
  endCall as endThunk,
  sendDigits as sendDigitsThunk,
  acceptCall as acceptThunk,
  startInternalCall as startInternalCallThunk,
  monitorCall as monitorCallThunk,
  exitMonitorCall as exitMonitorThunk,
  inviteCall as inviteCallThunk,
  cancelInviteCall as cancelInviteCallThunk,
  leaveCall as leaveCallThunk,
  restoreCall as restoreCallThunk,
  triggerReceiveKey as triggerReceiveKeyThunk,
  triggerSelfService as triggerSelfServiceThunk,
  invite as inviteThunk,
  kick as kickThunk,
  TransferParams,
  TriggerReceiveKeyParams,
  TriggerSelfServiceParams,
  SingleStepConferenceParams,
  mute as muteThunk,
  unmute as unmuteThunk,
} from '../store/sessions/call.thunk';
import { Session } from '../store/sessions';
import { muteMic as muteMicThunk, unmuteMic as unmuteMicThunk } from '../store/sessions/trtc.thunk';
import { getMessageList } from '../store/sessions/tim.thunk';
import { SessionAdapter } from './SessionAdapter';

export class Call extends SessionAdapter {
  startOutboundCall = async (params: Omit<Parameters<typeof callOutThunk>[0], 'emitter'>) => {
    const res = await store
      .dispatch(
        callOutThunk({
          ...params,
          emitter: this.sdk,
        }),
      )
      .then(unwrapResult);
    const {
      sessionId,
      remark,
      calleeLocation,
      calleePhoneNumber,
      callerPhoneNumber,
      protectedCallee,
      protectedCaller,
      aiEnabled,
      serverType,
    } = res;
    return {
      sessionId,
      remark,
      calleeLocation,
      calleePhoneNumber,
      callerPhoneNumber,
      protectedCallee,
      protectedCaller,
      aiEnabled,
      serverType,
    };
  };
  /**
   * @remark 回拨
   */
  dialBack = async (params: Omit<Parameters<typeof dialBackThunk>[0], 'emitter'>) => {
    const res = await store
      .dispatch(
        dialBackThunk({
          ...params,
          emitter: this.sdk,
        }),
      )
      .then(unwrapResult);
    const {
      sessionId,
      remark,
      calleeLocation,
      calleePhoneNumber,
      callerPhoneNumber,
      protectedCaller,
      protectedCallee,
      aiEnabled,
      serverType,
    } = res;
    return {
      sessionId,
      remark,
      calleeLocation,
      calleePhoneNumber,
      callerPhoneNumber,
      protectedCallee,
      protectedCaller,
      aiEnabled,
      serverType,
    };
  };

  startInternalCall = async (params: Omit<Parameters<typeof startInternalCallThunk>[0], 'emitter'>) => {
    const res = await store
      .dispatch(
        startInternalCallThunk({
          ...params,
          emitter: this.sdk,
        }),
      )
      .then(unwrapResult);
    const { sessionId, serverType } = res;
    return {
      sessionId,
      serverType,
    };
  };
  accept = async (params: Omit<Parameters<typeof acceptThunk>[0], 'emitter'>) => {
    const res = await store
      .dispatch(
        acceptThunk({
          ...params,
          emitter: this.sdk,
        }),
      )
      .then(unwrapResult);
    const { sessionId, aiEnabled } = res;
    return {
      sessionId,
      aiEnabled,
    };
  };
  hold = (params: Omit<Parameters<typeof holdThunk>[0], 'emitter'>) =>
    store.dispatch(holdThunk({ ...params, emitter: this.sdk })).then(unwrapResult);
  /**
   *  取消通话保持
   */
  unHold = (params: Omit<Parameters<typeof unHoldThunk>[0], 'emitter'>) =>
    store.dispatch(unHoldThunk({ ...params, emitter: this.sdk })).then(unwrapResult);
  transfer = (params: t.TypeOf<typeof TransferParams>) =>
    store.dispatch(transferThunk({ ...params, emitter: this.sdk })).then(unwrapResult);
  end = (params: Omit<Parameters<typeof endThunk>[0], 'closeBy' | 'emitter'>) =>
    store.dispatch(endThunk({ ...params, closeBy: 'seat', emitter: this.sdk })).then(unwrapResult);
  sendDigits = (params: Omit<Parameters<typeof sendDigitsThunk>[0], 'emitter'>) =>
    store.dispatch(sendDigitsThunk({ ...params, emitter: this.sdk })).then(unwrapResult);
  getConversationList = (...params: Parameters<typeof getMessageList>): Promise<Session[]> =>
    store.dispatch(getSessionList(...params)).unwrap();

  muteMic = (params: Parameters<typeof muteMicThunk>[0]) => store.dispatch(muteMicThunk(params)).then(unwrapResult);

  unmuteMic = (params: Parameters<typeof unmuteMicThunk>[0]) =>
    store.dispatch(unmuteMicThunk(params)).then(unwrapResult);

  deleteCall = (params: Omit<Parameters<typeof deleteCallThunk>[0], 'emitter'>) =>
    store.dispatch(deleteCallThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  monitorCall = (params: Omit<Parameters<typeof monitorCallThunk>[0], 'emitter'>) =>
    store.dispatch(monitorCallThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  exitMonitor = (params: Omit<Parameters<typeof exitMonitorThunk>[0], 'emitter'>) =>
    store.dispatch(exitMonitorThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  inviteCall = (params: Omit<Parameters<typeof inviteCallThunk>[0], 'emitter'>) =>
    store.dispatch(inviteCallThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  cancelInviteCall = (params: Omit<Parameters<typeof cancelInviteCallThunk>[0], 'emitter'>) =>
    store.dispatch(cancelInviteCallThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  restoreCall = (params: Omit<Parameters<typeof restoreCallThunk>[0], 'emitter'>) =>
    store.dispatch(restoreCallThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  leaveCall = (params: Omit<Parameters<typeof leaveCallThunk>[0], 'emitter'>) =>
    store.dispatch(leaveCallThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  triggerReceiveKey = (params: t.TypeOf<typeof TriggerReceiveKeyParams>) =>
    store.dispatch(triggerReceiveKeyThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  triggerSelfService = (params: t.TypeOf<typeof TriggerSelfServiceParams>) =>
    store.dispatch(triggerSelfServiceThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  invite = (params: t.TypeOf<typeof SingleStepConferenceParams>) =>
    store.dispatch(inviteThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  kick = (params: Omit<Parameters<typeof kickThunk>[0], 'emitter'>) =>
    store.dispatch(kickThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  mute = (params: Omit<Parameters<typeof muteThunk>[0], 'emitter'>) =>
    store.dispatch(muteThunk({ ...params, emitter: this.sdk })).then(unwrapResult);

  unmute = (params: Omit<Parameters<typeof unmuteThunk>[0], 'emitter'>) =>
    store.dispatch(unmuteThunk({ ...params, emitter: this.sdk })).then(unwrapResult);
}
